import { useEffect, useRef, useState } from "react";
import { X, Send } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { API, api } from "@/lib/api";
import { useAuth } from "@/store/auth";

const SUGGESTED_PUBLIC = [
  "How long do they last?",
  "What scents do you have?",
  "How's shipping?",
  "Do you take returns?",
];

const SUGGESTED_ADMIN = [
  "Show me today's revenue",
  "List all products",
  "Move Cherry Bomb to position 1",
  "Set Vanilla Vixen inventory to 50",
];

export default function AIChat() {
  const { user } = useAuth();
  const isOwner = user?.role === "owner";

  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const scrollRef = useRef(null);

  // Reset greeting when auth state changes
  useEffect(() => {
    setMessages([{
      id: `greet-${Date.now()}`,
      role: "assistant",
      text: isOwner
        ? `Hi ${user?.name?.split(" ")[0] || "boss"} — I'm Bree in admin mode. Tell me to move things, edit prices, restock, or ask for today's numbers.`
        : "Hey — I'm Bree. Ask me anything about our scents, shipping, or the vibe.",
    }]);
    setSessionId(null);
  }, [isOwner, user?.name]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, streaming]);

  const sendPublic = async (text) => {
    const now = Date.now();
    const userMsg = { id: `u-${now}`, role: "user", text };
    setMessages((m) => [...m, userMsg, { id: `a-${now}`, role: "assistant", text: "" }]);
    setInput(""); setStreaming(true);
    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId, message: text }),
      });
      if (!res.ok || !res.body) throw new Error("Chat unavailable");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop() || "";
        for (const part of parts) {
          const lines = part.split("\n");
          const dataLine = lines.find((l) => l.startsWith("data:"));
          const eventLine = lines.find((l) => l.startsWith("event:"));
          if (!dataLine) continue;
          const jsonStr = dataLine.slice(5).trim();
          if (!jsonStr) continue;
          try {
            const payload = JSON.parse(jsonStr);
            if (eventLine?.includes("session")) setSessionId(payload.session_id);
            else if (payload.delta) {
              setMessages((m) => {
                const copy = [...m];
                const last = copy[copy.length - 1];
                if (last.role === "assistant") copy[copy.length - 1] = { ...last, text: last.text + payload.delta };
                return copy;
              });
            } else if (payload.error) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", text: "Hmm — I hit a snag. Try again in a moment." };
                return copy;
              });
            }
          } catch (parseErr) {
            console.error("SSE payload parse error:", parseErr, jsonStr);
          }
        }
      }
    } catch (err) {
      console.error("Chat stream failed:", err);
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", text: "Hmm — I'm having trouble connecting." };
        return copy;
      });
    } finally { setStreaming(false); }
  };

  const sendAdmin = async (text) => {
    const now = Date.now();
    setMessages((m) => [...m, { id: `u-${now}`, role: "user", text }, { id: `a-${now}`, role: "assistant", text: "Working on it…", pending: true }]);
    setInput(""); setStreaming(true);
    try {
      const { data } = await api.post(
        "/admin/bree",
        { session_id: sessionId, message: text },
        { withCredentials: true }
      );
      setSessionId(data.session_id);
      const actionSummary =
        (data.actions && data.actions.length > 0)
          ? "\n\n" + data.actions.map((a, i) => {
              const res = data.results?.[i];
              if (res?.error) return `⚠️ ${a.type}: ${res.error}`;
              return `✓ ${a.type}${a.id ? ` (${a.id})` : ""}`;
            }).join("\n")
          : "";
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", text: (data.reply || "Done.") + actionSummary };
        return copy;
      });
    } catch (e) {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", text: "Admin action failed — try again." };
        return copy;
      });
    } finally { setStreaming(false); }
  };

  const send = async (text) => {
    if (!text.trim() || streaming) return;
    if (isOwner) await sendAdmin(text);
    else await sendPublic(text);
  };

  const suggested = isOwner ? SUGGESTED_ADMIN : SUGGESTED_PUBLIC;

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        data-testid="open-ai-chat-btn"
        className={`fixed bottom-6 right-6 z-30 flex items-center gap-2 pl-1.5 pr-5 h-14 rounded-full bg-[#FF3D8B] text-black font-bold uppercase tracking-wider text-xs shadow-[0_0_40px_rgba(255,61,139,0.5)] hover:bg-[#ff5ba0] transition-colors ${open ? "hidden" : ""}`}
      >
        <img src="/bree-avatar.png" alt="" className="w-11 h-11 rounded-full object-cover border-2 border-black" />
        {isOwner ? "Bree · Admin" : "Ask Bree"}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ type: "spring", damping: 24, stiffness: 300 }}
            className="fixed bottom-6 right-6 z-30 w-[92vw] sm:w-[400px] h-[560px] rounded-2xl bg-black/90 backdrop-blur-2xl border border-[#FF3D8B]/40 shadow-[0_0_60px_rgba(255,61,139,0.35)] flex flex-col overflow-hidden"
            data-testid="ai-chat-panel"
          >
            <header className="flex items-center gap-3 px-4 py-4 border-b border-white/10 bg-gradient-to-b from-[#FF3D8B]/10 to-transparent">
              <div className="relative">
                <img src="/bree-avatar.png" alt="Bree"
                  className="w-11 h-11 rounded-full object-cover border border-[#FF3D8B]/60 shadow-[0_0_15px_rgba(255,61,139,0.5)]"
                  data-testid="bree-avatar" />
                <span className="absolute bottom-0 right-0 w-2.5 h-2.5 rounded-full bg-[#FF3D8B] ring-2 ring-black" />
              </div>
              <div className="flex-1">
                <div className="font-display text-lg text-neon-pink leading-none">Bree</div>
                <div className="text-white/50 text-[11px] mt-1">
                  {isOwner ? "Admin mode · database access" : "Booty Breeze concierge · online"}
                </div>
              </div>
              <button onClick={() => setOpen(false)} className="text-white/60 hover:text-white" data-testid="close-ai-chat-btn">
                <X size={20} />
              </button>
            </header>

            <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
              {messages.map((m, i) => (
                <div key={m.id || `msg-${i}`} className={`flex gap-2 items-end ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  {m.role === "assistant" && (
                    <img src="/bree-avatar.png" alt="" className="w-7 h-7 rounded-full object-cover border border-[#FF3D8B]/40 flex-shrink-0" />
                  )}
                  <div
                    className={`max-w-[78%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${
                      m.role === "user" ? "bg-[#FF3D8B] text-black rounded-br-sm" : "bg-white/5 text-white border border-white/10 rounded-bl-sm"
                    }`}
                    data-testid={`chat-msg-${m.role}`}
                  >
                    {m.text || (streaming ? "…" : "")}
                  </div>
                </div>
              ))}
              {messages.length === 1 && (
                <div className="flex flex-wrap gap-2 pt-2">
                  {suggested.map((s) => (
                    <button key={s} onClick={() => send(s)}
                      className="text-xs px-3 py-1.5 rounded-full border border-white/20 text-white/80 hover:border-[#FF3D8B] hover:text-[#FF3D8B] transition-colors"
                      data-testid={`chat-suggestion-${s.slice(0,10)}`}>
                      {s}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <form onSubmit={(e) => { e.preventDefault(); send(input); }} className="border-t border-white/10 p-3 flex gap-2">
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder={isOwner ? "Tell me what to do…" : "Ask me anything…"}
                data-testid="chat-input"
                className="flex-1 bg-white/5 border border-white/10 rounded-full px-4 py-2.5 text-sm text-white placeholder:text-white/40 focus:outline-none focus:border-[#FF3D8B]"
              />
              <button type="submit" disabled={streaming || !input.trim()} data-testid="chat-send-btn"
                className="w-11 h-11 rounded-full bg-[#FF3D8B] text-black flex items-center justify-center disabled:opacity-40 hover:bg-[#ff5ba0] transition-colors">
                <Send size={16} />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
